import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Section } from "../Section";
import * as styles from "./Alert.module.scss";

const defaultClassNames = {
	heading: styles.alertHeading,
	body: styles.alertBody,
};

const Alert = ({
	className,
	classNames: providedClassNames,
	children,
	heading,
	headingAs: HeadingTag,
	defaultStyle,
	variant,
	hide,
	labels,
	dismissible,
	dismissibleIcon,
	icon,
	textWrapper,
	onClick,
}) => {
	const [showAlert, setShowAlert] = useState(!hide);

	useEffect(() => {
		setShowAlert(!hide);
	}, [hide]);

	const classNames = { ...defaultClassNames, ...providedClassNames };

	const classes = cx({
		[styles.alert]: defaultStyle,
		[styles[variant]]: defaultStyle && variant && styles[variant],
		[className]: className,
	});

	const AlertBodyRender = (
		<div className={cx(styles.alertFlexWrapper, classNames?.wrapper)}>
			<div
				className={cx(
					styles.alertBody,
					dismissible && styles.alertIsDismissible,
					classNames?.body
				)}>
				{icon && icon}
				<div className={classNames?.headingContainer}>
					{heading && (
						<HeadingTag
							className={cx(
								styles.alertHeading,
								!children && styles.noChildren,
								classNames.heading
							)}>
							{heading}
						</HeadingTag>
					)}
					{children}
				</div>
			</div>
			{dismissible && (
				<button
					type="button"
					className={styles.alertClose}
					onClick={() => {
						setShowAlert(false);
						if (typeof onClick === "function") {
							onClick();
						}
					}}
					aria-label={labels}>
					{dismissibleIcon}
				</button>
			)}
		</div>
	);

	return (
		showAlert && (
			<div className={classes}>
				{textWrapper ? (
					<Section
						padding="none"
						theme="none"
						className={cx(styles.alertBannerWrapper, dismissible && styles.alertIsDismissible)}>
						{AlertBodyRender}
					</Section>
				) : (
					AlertBodyRender
				)}
			</div>
		)
	);
};

Alert.propTypes = {
	className: PropTypes.string,
	classNames: PropTypes.shape({
		wrapper: PropTypes.string,
		heading: PropTypes.string,
		headingContainer: PropTypes.string,
		body: PropTypes.string,
	}),
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	heading: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	headingAs: PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5", "h6", "p", "div"]),
	defaultStyle: PropTypes.bool,
	variant: PropTypes.oneOf(["success", "danger", "warning", "info", "none"]),
	hide: PropTypes.bool,
	dismissible: PropTypes.bool,
	labels: PropTypes.string,
	dismissibleIcon: PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.string]),
	icon: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
	textWrapper: PropTypes.bool,
	onClick: PropTypes.func,
};

Alert.defaultProps = {
	className: undefined,
	classNames: defaultClassNames,
	children: undefined,
	heading: undefined,
	headingAs: "div",
	defaultStyle: true,
	variant: "danger",
	hide: false,
	dismissible: true,
	labels: "Close alert",
	dismissibleIcon: "×",
	icon: undefined,
	textWrapper: false,
	onClick: () => {},
};

export default Alert;
export { Alert };
